import type { IconName } from '../../icons/Icon';
import { normalizeStatusValue } from '../../data/StatusBadge/registry';

export type NextActionTone = 'default' | 'primary' | 'danger';

/** The one thing to do next with a record in its current status. */
export interface NextAction {
  label: string;
  /** Design-system catalogue key for `label` (C3); the viewer's text replaces the English `label` on screen. */
  labelKey?: string;
  /** One sentence under the next-step hero's title: why this, now (W28). */
  description?: string;
  descriptionKey?: string;
  icon?: IconName;
  /** Navigates when set; otherwise the button calls `onClick` / the component's `onAction`. */
  href?: string;
  onClick?: () => void;
  /** Permission key checked through `can` / `NextActionPermissionsProvider`. */
  permission?: string;
  tone?: NextActionTone;
}

/** Route builders the app supplies; keys are action names, values build the URL for an id. */
export type NextActionRoutes = Record<string, ((id: string) => string) | undefined>;

export interface NextActionContext {
  id: string;
  /** Normalised backend status (`IN_TRANSIT`). */
  status: string;
  routes: NextActionRoutes;
  /** Anything else a builder may need (the row, the user…). */
  extra?: Record<string, unknown>;
}

export type NextActionBuilder = (ctx: NextActionContext) => NextAction | undefined;

const builders = new Map<string, NextActionBuilder>();

/** Registers the builder for an entity type; the returned function unregisters it. */
export function registerNextActions(entityType: string, builder: NextActionBuilder): () => void {
  const key = entityType.trim();
  builders.set(key, builder);
  return () => {
    if (builders.get(key) === builder) builders.delete(key);
  };
}

/**
 * Declares next actions as `status → action` and registers them. The resolver receives the normalised
 * status (`IN_TRANSIT`) and the context so it can build hrefs from the app's routes.
 */
export function defineNextActions(entityType: string, resolve: (status: string, ctx: NextActionContext) => NextAction | undefined): NextActionBuilder {
  const builder: NextActionBuilder = (ctx) => resolve(ctx.status, ctx);
  registerNextActions(entityType, builder);
  return builder;
}

export function hasNextActions(entityType: string): boolean {
  return builders.has(entityType.trim());
}

/** Resolves the next action for a record; `undefined` when the entity or status has none. */
export function resolveNextAction(entityType: string, status: string, ctx: { id: string; routes?: NextActionRoutes; extra?: Record<string, unknown> }): NextAction | undefined {
  const builder = builders.get(entityType.trim());
  if (!builder) return undefined;
  return builder({ id: ctx.id, status: normalizeStatusValue(status), routes: ctx.routes ?? {}, extra: ctx.extra });
}

export function listNextActionEntities(): string[] {
  return Array.from(builders.keys());
}

/** Test helper: removes every builder (the baseline maps re-register when `baseline.ts` is imported again). */
export function resetNextActions(): void {
  builders.clear();
}
